import React, { useCallback, useEffect, useRef, useState } from "react";

import { brandsService } from "api/services/brands.service";
import { lessonProductsService } from "api/services/lesson-products.service";
import Button from "components/UI/Button";
import { Select } from "components/UI/Input";
import RichTextEditor from "components/UI/RichTextEditor";

import type { Brand } from "api/services/brands.service";
import type { LessonProductDetail, LessonProductImage, TicketType } from "api/types/lesson-product.types";

// ─── Constants ────────────────────────────────────────────────────────────────
export const TICKET_TYPE_LABELS: Record<TicketType, string> = {
  NONE: "일반 강습권",
  SPECIAL_111: "ONEELEVEN CLASS",
  SPECIAL_11: "ELEVEN CLASS",
  SPECIAL_1: "ONE CLASS",
  ONE_DAY: "체험권 (1회)",
  GENERAL_1T10: "특강권 1~10회권",
};

const TICKET_TYPE_OPTIONS = Object.entries(TICKET_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

// ─── 옵션 패널 로컬 타입 ──────────────────────────────────────────────────────
interface LocalValue {
  id?: number;
  tempId: number;
  name: string;
  price_delta: number;
  is_active: boolean;
  isNew: boolean;
  originalIsActive: boolean;
  _toDelete: boolean;
  _modified: boolean;
  isEditing: boolean;
  editName: string;
  editPriceDelta: string;
}

interface LocalGroup {
  id?: number;
  tempId: number;
  name: string;
  is_required: boolean;
  allow_multiple: boolean;
  is_active: boolean;
  isNew: boolean;
  originalIsActive: boolean;
  _toDelete: boolean;
  _modified: boolean;
  isEditing: boolean;
  editName: string;
  editIsRequired: boolean;
  editAllowMultiple: boolean;
  values: LocalValue[];
  newValueName: string;
  newValuePriceDelta: string;
}

// ─── 옵션 관리 패널 ───────────────────────────────────────────────────────────
const OptionsPanel: React.FC<{ productId: number }> = ({ productId }) => {
  const [groups, setGroups] = useState<LocalGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await lessonProductsService.getOptionGroups(productId);
      setGroups(
        res.data.map((g) => ({
          id: g.id,
          tempId: g.id,
          name: g.name,
          is_required: g.is_required,
          allow_multiple: g.allow_multiple,
          is_active: g.is_active,
          isNew: false,
          originalIsActive: g.is_active,
          _toDelete: false,
          _modified: false,
          isEditing: false,
          editName: g.name,
          editIsRequired: g.is_required,
          editAllowMultiple: g.allow_multiple,
          values: g.values.map((v) => ({
            id: v.id,
            tempId: v.id,
            name: v.name,
            price_delta: Number(v.price_delta),
            is_active: v.is_active,
            isNew: false,
            originalIsActive: v.is_active,
            _toDelete: false,
            _modified: false,
            isEditing: false,
            editName: v.name,
            editPriceDelta: String(Number(v.price_delta)),
          })),
          newValueName: "",
          newValuePriceDelta: "",
        })),
      );
    } catch {
      setError("옵션을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // ── 그룹 조작 ────────────────────────────────────────────────────────────────
  const addGroup = () => {
    setGroups((prev) => [
      ...prev,
      {
        id: undefined,
        tempId: Date.now(),
        name: "",
        is_required: true,
        allow_multiple: false,
        is_active: true,
        isNew: true,
        originalIsActive: true,
        _toDelete: false,
        _modified: false,
        isEditing: true,
        editName: "",
        editIsRequired: false,
        editAllowMultiple: false,
        values: [],
        newValueName: "",
        newValuePriceDelta: "",
      },
    ]);
  };

  const updateGroupField = (tempId: number, field: string, value: unknown) => {
    setGroups((prev) => prev.map((g) => (g.tempId === tempId ? { ...g, [field]: value } : g)));
  };

  const startEditGroup = (tempId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId === tempId
          ? {
              ...g,
              isEditing: true,
              editName: g.name,
              editIsRequired: g.is_required,
              editAllowMultiple: g.allow_multiple,
            }
          : g,
      ),
    );
  };

  const confirmEditGroup = (tempId: number) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.tempId !== tempId) return g;
        if (!g.editName.trim()) return g;
        return {
          ...g,
          isEditing: false,
          name: g.editName.trim(),
          is_required: g.editIsRequired,
          allow_multiple: g.editAllowMultiple,
          _modified: !g.isNew,
        };
      }),
    );
  };

  const cancelEditGroup = (tempId: number) => {
    setGroups((prev) => prev.map((g) => (g.tempId === tempId ? { ...g, isEditing: false } : g)));
  };

  const removeGroup = (tempId: number) => {
    setGroups((prev) => {
      const group = prev.find((g) => g.tempId === tempId);
      if (!group) return prev;
      if (group.isNew) return prev.filter((g) => g.tempId !== tempId);
      if (!window.confirm("이 옵션 그룹을 삭제(비활성화) 처리하시겠습니까? 저장 시 반영됩니다.")) return prev;
      return prev.map((g) => (g.tempId === tempId ? { ...g, _toDelete: true, isEditing: false } : g));
    });
  };

  const undoRemoveGroup = (tempId: number) => {
    setGroups((prev) => prev.map((g) => (g.tempId === tempId ? { ...g, _toDelete: false } : g)));
  };

  const deactivateGroup = (tempId: number) => {
    if (!window.confirm("이 옵션 그룹을 비활성화하면 모든 옵션 값도 함께 비활성화됩니다. 계속하시겠습니까?")) return;
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId === tempId
          ? {
              ...g,
              is_active: false,
              _modified: !g.isNew,
              values: g.values.map((v) => ({ ...v, is_active: false })),
            }
          : g,
      ),
    );
  };

  const activateGroup = (tempId: number) => {
    alert("그룹을 활성화합니다. 활성화 후 필요한 옵션 값을 추가해주세요.");
    setGroups((prev) => prev.map((g) => (g.tempId === tempId ? { ...g, is_active: true, _modified: !g.isNew } : g)));
  };

  // ── 값 조작 ──────────────────────────────────────────────────────────────────
  const addValueToGroup = (groupTempId: number) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.tempId !== groupTempId || !g.newValueName.trim()) return g;
        const newVal: LocalValue = {
          id: undefined,
          tempId: Date.now(),
          name: g.newValueName.trim(),
          price_delta: Number(g.newValuePriceDelta) || 0,
          is_active: true,
          isNew: true,
          originalIsActive: true,
          _toDelete: false,
          _modified: false,
          isEditing: false,
          editName: g.newValueName.trim(),
          editPriceDelta: g.newValuePriceDelta,
        };
        return { ...g, values: [...g.values, newVal], newValueName: "", newValuePriceDelta: "" };
      }),
    );
  };

  const startEditValue = (groupTempId: number, valueTempId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId !== groupTempId
          ? g
          : {
              ...g,
              values: g.values.map((v) =>
                v.tempId === valueTempId
                  ? {
                      ...v,
                      isEditing: true,
                      editName: v.name,
                      editPriceDelta: String(v.price_delta),
                    }
                  : v,
              ),
            },
      ),
    );
  };

  const confirmEditValue = (groupTempId: number, valueTempId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId !== groupTempId
          ? g
          : {
              ...g,
              values: g.values.map((v) => {
                if (v.tempId !== valueTempId) return v;
                if (!v.editName.trim()) return v;
                return {
                  ...v,
                  isEditing: false,
                  name: v.editName.trim(),
                  price_delta: Number(v.editPriceDelta) || 0,
                  _modified: !v.isNew,
                };
              }),
            },
      ),
    );
  };

  const cancelEditValue = (groupTempId: number, valueTempId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId !== groupTempId
          ? g
          : {
              ...g,
              values: g.values.map((v) => (v.tempId === valueTempId ? { ...v, isEditing: false } : v)),
            },
      ),
    );
  };

  const removeValue = (groupTempId: number, valueTempId: number) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.tempId !== groupTempId) return g;
        const val = g.values.find((v) => v.tempId === valueTempId);
        if (!val) return g;
        if (val.isNew) return { ...g, values: g.values.filter((v) => v.tempId !== valueTempId) };
        return {
          ...g,
          values: g.values.map((v) => (v.tempId === valueTempId ? { ...v, _toDelete: true, isEditing: false } : v)),
        };
      }),
    );
  };

  const undoRemoveValue = (groupTempId: number, valueTempId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId !== groupTempId
          ? g
          : {
              ...g,
              values: g.values.map((v) => (v.tempId === valueTempId ? { ...v, _toDelete: false } : v)),
            },
      ),
    );
  };

  const deactivateValue = (groupTempId: number, valueTempId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId === groupTempId
          ? {
              ...g,
              values: g.values.map((v) => (v.tempId === valueTempId ? { ...v, is_active: false } : v)),
            }
          : g,
      ),
    );
  };

  const activateValue = (groupTempId: number, valueTempId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.tempId === groupTempId
          ? {
              ...g,
              values: g.values.map((v) => (v.tempId === valueTempId ? { ...v, is_active: true } : v)),
            }
          : g,
      ),
    );
  };

  // ── 저장 (API 일괄 호출) ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      for (const group of groups) {
        if (group._toDelete && !group.isNew && group.id) {
          await lessonProductsService.deleteOptionGroup(productId, group.id);
          continue;
        }

        if (group.isNew && !group._toDelete) {
          if (!group.name.trim()) continue;
          const gRes = await lessonProductsService.createOptionGroup(productId, {
            name: group.name.trim(),
            is_required: group.is_required,
            allow_multiple: group.allow_multiple,
            is_active: group.is_active,
          });
          const newGroupId = gRes.data.id;
          for (const val of group.values) {
            if (val._toDelete || !val.name.trim() || !val.is_active) continue;
            await lessonProductsService.createOptionValue(productId, newGroupId, {
              name: val.name.trim(),
              price_delta: val.price_delta,
            });
          }
        } else if (!group.isNew && group.id) {
          const needsUpdate = group._modified || group.is_active !== group.originalIsActive;
          if (needsUpdate) {
            await lessonProductsService.updateOptionGroup(productId, group.id, {
              name: group.name,
              is_required: group.is_required,
              allow_multiple: group.allow_multiple,
              is_active: group.is_active,
            });
          }
          for (const val of group.values) {
            if (val._toDelete && !val.isNew && val.id) {
              await lessonProductsService.deleteOptionValue(productId, group.id, val.id);
            } else if (val.isNew && !val._toDelete && val.is_active && val.name.trim()) {
              await lessonProductsService.createOptionValue(productId, group.id, {
                name: val.name.trim(),
                price_delta: val.price_delta,
              });
            } else if (!val.isNew && !val._toDelete && val.id) {
              const valNeedsUpdate = val._modified || val.is_active !== val.originalIsActive;
              if (valNeedsUpdate) {
                await lessonProductsService.updateOptionValue(productId, group.id, val.id, {
                  name: val.name,
                  price_delta: val.price_delta,
                  is_active: val.is_active,
                });
              }
            }
          }
        }
      }
      await loadGroups();
    } catch {
      setError("저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="cl-options-status">불러오는 중...</p>;

  return (
    <div className="cl-options-panel">
      {error && <p className="cl-options-status cl-options-status--error">{error}</p>}

      {groups.length === 0 && <p className="cl-options-status">등록된 옵션 그룹이 없습니다.</p>}

      {groups.map((group) => (
        <div key={group.tempId} className={`cl-option-group ${!group.is_active && !group._toDelete ? "cl-option-group--inactive" : ""} ${group._toDelete ? "cl-option-group--delete" : ""}`}>
          <div className="cl-option-group__header">
            {group._toDelete ? (
              <>
                <span className="cl-option-group__name cl-option-group__name--deleted">{group.name}</span>
                <span className="cl-badge cl-badge--delete">삭제 예정</span>
                <button type="button" className="cl-btn-undo" onClick={() => undoRemoveGroup(group.tempId)}>
                  되돌리기
                </button>
              </>
            ) : group.isEditing ? (
              <>
                <input
                  className="cl-input"
                  placeholder="옵션 그룹명 (예: 인원수)"
                  value={group.editName}
                  onChange={(e) => updateGroupField(group.tempId, "editName", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") confirmEditGroup(group.tempId);
                  }}
                  style={{ flex: 1 }}
                  autoFocus
                />
                <label className="cl-checkbox-label">
                  <input type="checkbox" checked={group.editIsRequired} onChange={(e) => updateGroupField(group.tempId, "editIsRequired", e.target.checked)} />
                  필수
                </label>
                <label className="cl-checkbox-label">
                  <input type="checkbox" checked={group.editAllowMultiple} onChange={(e) => updateGroupField(group.tempId, "editAllowMultiple", e.target.checked)} />
                  다중선택
                </label>
                <button type="button" className="cl-btn-confirm" onClick={() => confirmEditGroup(group.tempId)}>
                  확인
                </button>
                {!group.isNew && (
                  <button type="button" className="cl-btn-cancel" onClick={() => cancelEditGroup(group.tempId)}>
                    취소
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="cl-option-group__name">{group.name}</span>
                <div className="cl-option-group__badges">
                  {group.is_required && <span className="cl-badge cl-badge--required">필수</span>}
                  {group.allow_multiple && <span className="cl-badge cl-badge--multiple">다중선택</span>}
                  {!group.is_active && <span className="cl-badge cl-badge--inactive">비활성</span>}
                  {group._modified && <span className="cl-badge cl-badge--new">수정됨</span>}
                </div>
                <button type="button" className="cl-btn-edit-sm" onClick={() => startEditGroup(group.tempId)}>
                  수정
                </button>
                {group.is_active ? (
                  <button type="button" className="cl-btn-deactivate" onClick={() => deactivateGroup(group.tempId)}>
                    비활성화
                  </button>
                ) : (
                  <button type="button" className="cl-btn-activate" onClick={() => activateGroup(group.tempId)}>
                    활성화
                  </button>
                )}
                <button type="button" className="cl-btn-remove-group" onClick={() => removeGroup(group.tempId)}>
                  삭제
                </button>
              </>
            )}
          </div>

          {!group._toDelete && group.is_active && (
            <div className="cl-option-values">
              {group.values.filter((v) => v.is_active && !v._toDelete).length === 0 && <p className="cl-options-hint">옵션 값을 입력해주세요.</p>}

              {/* 활성 값 */}
              {group.values
                .filter((v) => v.is_active && !v._toDelete)
                .map((val) => (
                  <div key={val.tempId} className={`cl-option-value-row ${val.isNew ? "cl-option-value-row--new" : ""}`}>
                    {val.isEditing ? (
                      <>
                        <input
                          className="cl-input"
                          value={val.editName}
                          onChange={(e) =>
                            setGroups((prev) =>
                              prev.map((g) =>
                                g.tempId !== group.tempId
                                  ? g
                                  : {
                                      ...g,
                                      values: g.values.map((v) => (v.tempId === val.tempId ? { ...v, editName: e.target.value } : v)),
                                    },
                              ),
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") confirmEditValue(group.tempId, val.tempId);
                          }}
                          style={{ flex: 1 }}
                          autoFocus
                        />
                        <div className="cl-input-suffix">
                          <input
                            className="cl-input cl-input--right"
                            type="number"
                            value={val.editPriceDelta}
                            onChange={(e) =>
                              setGroups((prev) =>
                                prev.map((g) =>
                                  g.tempId !== group.tempId
                                    ? g
                                    : {
                                        ...g,
                                        values: g.values.map((v) => (v.tempId === val.tempId ? { ...v, editPriceDelta: e.target.value } : v)),
                                      },
                                ),
                              )
                            }
                            style={{ width: 90 }}
                          />
                          <span className="cl-suffix">원</span>
                        </div>
                        <button type="button" className="cl-btn-confirm" onClick={() => confirmEditValue(group.tempId, val.tempId)}>
                          확인
                        </button>
                        <button type="button" className="cl-btn-cancel" onClick={() => cancelEditValue(group.tempId, val.tempId)}>
                          취소
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="cl-option-value-row__name">{val.name}</span>
                        <span className="cl-option-value-row__price">{val.price_delta > 0 ? `+${val.price_delta.toLocaleString()}원` : "-"}</span>
                        {val.isNew && <span className="cl-badge cl-badge--new">미저장</span>}
                        {val._modified && <span className="cl-badge cl-badge--new">수정됨</span>}
                        <button type="button" className="cl-btn-edit-sm" onClick={() => startEditValue(group.tempId, val.tempId)}>
                          수정
                        </button>
                        <button type="button" className="cl-btn-deactivate cl-btn-deactivate--sm" onClick={() => deactivateValue(group.tempId, val.tempId)}>
                          비활성화
                        </button>
                        <button type="button" className="cl-btn-remove-sm" onClick={() => removeValue(group.tempId, val.tempId)}>
                          삭제
                        </button>
                      </>
                    )}
                  </div>
                ))}

              {/* 삭제 예정 값 */}
              {group.values
                .filter((v) => v._toDelete)
                .map((val) => (
                  <div key={val.tempId} className="cl-option-value-row cl-option-value-row--delete">
                    <span className="cl-option-value-row__name cl-option-value-row__name--deleted">{val.name}</span>
                    <span className="cl-badge cl-badge--delete">삭제 예정</span>
                    <button type="button" className="cl-btn-undo cl-btn-undo--sm" onClick={() => undoRemoveValue(group.tempId, val.tempId)}>
                      되돌리기
                    </button>
                  </div>
                ))}

              {/* 비활성 값 (활성화 가능) */}
              {group.values
                .filter((v) => !v.is_active && !v._toDelete)
                .map((val) => (
                  <div key={val.tempId} className="cl-option-value-row cl-option-value-row--inactive">
                    <span className="cl-option-value-row__name">{val.name}</span>
                    <span className="cl-option-value-row__status">비활성</span>
                    <button type="button" className="cl-btn-activate cl-btn-activate--sm" onClick={() => activateValue(group.tempId, val.tempId)}>
                      활성화
                    </button>
                  </div>
                ))}

              {/* 새 값 입력 행 */}
              <div className="cl-option-value-add-row">
                <input
                  className="cl-input"
                  placeholder="새 옵션값 (예: 1인)"
                  value={group.newValueName}
                  onChange={(e) => updateGroupField(group.tempId, "newValueName", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addValueToGroup(group.tempId);
                  }}
                />
                <div className="cl-input-suffix">
                  <input className="cl-input cl-input--right" type="number" placeholder="0" value={group.newValuePriceDelta} onChange={(e) => updateGroupField(group.tempId, "newValuePriceDelta", e.target.value)} style={{ width: 100 }} />
                  <span className="cl-suffix">원</span>
                </div>
                <Button variant="secondary" size="sm" onClick={() => addValueToGroup(group.tempId)}>
                  추가
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="cl-options-footer">
        <Button variant="outline" size="lg" onClick={addGroup}>
          + 옵션 그룹 추가
        </Button>
        <Button variant="primary" size="lg" disabled={saving} onClick={handleSave}>
          {saving ? "저장 중..." : "저장"}
        </Button>
      </div>
    </div>
  );
};

// ─── 편집 모달 ────────────────────────────────────────────────────────────────
export interface ClassEditModalProps {
  product: LessonProductDetail;
  onSave: () => void;
  onClose: () => void;
}

const ClassEditModal: React.FC<ClassEditModalProps> = ({ product, onSave, onClose }) => {
  const [activeTab, setActiveTab] = useState<"info" | "options">("info");

  const [name, setName] = useState(product.name);
  const [brand, setBrand] = useState(product.brand);
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    brandsService
      .list()
      .then((res) => setBrands(res.data))
      .catch(() => {
        /* ignore */
      });
  }, []);
  const [sessionsCount, setSessionsCount] = useState(String(product.sessions_count));
  const [price, setPrice] = useState(product.price);
  const [discountPrice, setDiscountPrice] = useState(product.discount_price || "");
  const [validityPeriod, setValidityPeriod] = useState(String(product.validity_period));
  const [ticketType, setTicketType] = useState<TicketType>(product.ticket_type || "NONE");
  const [shortDescription, setShortDescription] = useState(product.short_description || "");
  const [detailedContent, setDetailedContent] = useState(product.detailed_content || "");
  const [isActive, setIsActive] = useState(product.is_active);
  const [isFeatured, setIsFeatured] = useState(product.is_featured);
  const [isRetakeProduct, setIsRetakeProduct] = useState(product.is_retake_product);
  const [theoryRequiredBeforeDay, setTheoryRequiredBeforeDay] = useState(product.theory_required_before_day != null ? String(product.theory_required_before_day) : "");

  // 이미지
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [removedImageIds, setRemovedImageIds] = useState<Set<number>>(new Set());
  const [newImages, setNewImages] = useState<File[]>([]);
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);

  const existingVisible = product.images.filter((img): img is LessonProductImage & { image: string } => !!img.image && !removedImageIds.has(img.id)).sort((a, b) => a.order - b.order);

  const totalImageCount = existingVisible.length + newImages.length;
  const imagesChanged = removedImageIds.size > 0 || newImages.length > 0;

  // 상세 내용 에디터 이미지 업로드 → 서버 URL 반환
  const uploadDetailImage = async (file: File): Promise<string> => {
    const { data } = await lessonProductsService.uploadImage(file);
    return data.url;
  };

  const handleAddImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const combined = [...newImages, ...files].slice(0, 10 - existingVisible.length);
    setNewImages(combined);
    setNewImagePreviews(combined.map((f) => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const handleRemoveExisting = (id: number) => {
    setRemovedImageIds((prev) => new Set(prev).add(id));
  };

  const handleRemoveNew = (idx: number) => {
    URL.revokeObjectURL(newImagePreviews[idx]);
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handleSave = async () => {
    if (!name.trim()) {
      setSaveError("강습명을 입력해주세요.");
      return;
    }
    if (!price || Number(price) <= 0) {
      setSaveError("정가를 입력해주세요.");
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      let imagePayload: { images?: File[]; image_thumbnails?: boolean[]; image_orders?: number[] } = {};

      if (imagesChanged) {
        const existingFiles = await Promise.all(
          existingVisible.map(async (img) => {
            const res = await fetch(img.image);
            const blob = await res.blob();
            return new File([blob], img.image.split("/").pop() || "image.jpg", {
              type: blob.type,
            });
          }),
        );
        const allImages = [...existingFiles, ...newImages];
        imagePayload = {
          images: allImages,
          image_thumbnails: allImages.map((_, i) => i === 0),
          image_orders: allImages.map((_, i) => i),
        };
      }

      await lessonProductsService.update(product.id, {
        name: name.trim(),
        brand: brand.trim(),
        sessions_count: Number(sessionsCount) || 1,
        price,
        discount_price: discountPrice || undefined,
        validity_period: Number(validityPeriod) || 365,
        ticket_type: ticketType,
        theory_required_before_day: theoryRequiredBeforeDay ? Number(theoryRequiredBeforeDay) : null,
        short_description: shortDescription,
        detailed_content: detailedContent,
        is_active: isActive,
        is_featured: isFeatured,
        is_retake_product: isRetakeProduct,
        ...imagePayload,
      });
      onSave();
    } catch (err) {
      const data = (err as { response?: { data?: { detail?: string } & Record<string, unknown> } })?.response?.data;
      const msg =
        data?.detail ||
        Object.values(data || {})
          .flat()
          .join(", ") ||
        "저장 중 오류가 발생했습니다.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* 탭 */}
      <div className="cl-modal-tabs">
        <button className={`cl-modal-tab ${activeTab === "info" ? "cl-modal-tab--active" : ""}`} onClick={() => setActiveTab("info")}>
          기본 정보
        </button>
        <button className={`cl-modal-tab ${activeTab === "options" ? "cl-modal-tab--active" : ""}`} onClick={() => setActiveTab("options")}>
          강습 옵션
        </button>
      </div>

      {/* 기본 정보 탭 */}
      {activeTab === "info" && (
        <div className="cl-modal-info">
          {saveError && <p className="cl-error">{saveError}</p>}

          <div className="cl-form-row">
            <label className="cl-label">강습명 *</label>
            <input className="cl-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="cl-form-row">
            <label className="cl-label">브랜드</label>
            <Select value={brand} onChange={(e) => setBrand(e.target.value)} options={[{ value: "", label: "브랜드를 선택해주세요" }, ...brands.map((b) => ({ value: b.name, label: b.name }))]} />
          </div>
          {/* 이미지 */}
          <div className="cl-form-row">
            <label className="cl-label">썸네일</label>
            <div className="cl-image-upload">
              {existingVisible.map((img, i) => (
                <div key={img.id} className="cl-image-upload__item cl-image-upload__item--filled">
                  <img src={img.image} alt="" />
                  {i === 0 && <span className="cl-image-upload__thumb-label">썸네일</span>}
                  <button type="button" className="cl-image-upload__remove" onClick={() => handleRemoveExisting(img.id)}>
                    ✕
                  </button>
                </div>
              ))}
              {newImagePreviews.map((src, i) => (
                <div key={`new-${i}`} className="cl-image-upload__item cl-image-upload__item--filled cl-image-upload__item--new">
                  <img src={src} alt="" />
                  {existingVisible.length === 0 && i === 0 && <span className="cl-image-upload__thumb-label">썸네일</span>}
                  <button type="button" className="cl-image-upload__remove" onClick={() => handleRemoveNew(i)}>
                    ✕
                  </button>
                </div>
              ))}
              {totalImageCount < 10 && (
                <div className="cl-image-upload__item cl-image-upload__item--add" onClick={() => imageInputRef.current?.click()}>
                  <span>＋</span>
                  <span>이미지 추가</span>
                </div>
              )}
            </div>
            <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleAddImages} />
          </div>
          <div className="cl-form-row">
            <label className="cl-label">짧은 설명</label>
            <input className="cl-input" value={shortDescription} maxLength={100} onChange={(e) => setShortDescription(e.target.value)} />
          </div>
          <div className="cl-form-row">
            <label className="cl-label">상세 내용</label>
            <RichTextEditor value={detailedContent} onChange={setDetailedContent} uploadImage={uploadDetailImage} placeholder="상세 내용을 입력해주세요" />
          </div>

          <div className="cl-form-grid-2">
            <div className="cl-form-row">
              <label className="cl-label">세션 수</label>
              <div className="cl-input-suffix">
                <input className="cl-input cl-input--right" type="number" min="1" value={sessionsCount} onChange={(e) => setSessionsCount(e.target.value)} />
                <span className="cl-suffix">회</span>
              </div>
            </div>
            <div className="cl-form-row">
              <label className="cl-label">유효기간</label>
              <div className="cl-input-suffix">
                <input className="cl-input cl-input--right" type="number" min="1" value={validityPeriod} onChange={(e) => setValidityPeriod(e.target.value)} />
                <span className="cl-suffix">일</span>
              </div>
            </div>
          </div>

          <div className="cl-form-grid-2">
            <div className="cl-form-row">
              <label className="cl-label">정가 *</label>
              <div className="cl-input-suffix">
                <input className="cl-input cl-input--right" type="text" value={price ? Number(price).toLocaleString("ko-KR") : ""} onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))} />
                <span className="cl-suffix">원</span>
              </div>
            </div>
            <div className="cl-form-row">
              <label className="cl-label">할인가</label>
              <div className="cl-input-suffix">
                <input className="cl-input cl-input--right" type="text" placeholder="없으면 비워두세요" value={discountPrice ? Number(discountPrice).toLocaleString("ko-KR") : ""} onChange={(e) => setDiscountPrice(e.target.value.replace(/[^0-9]/g, ""))} />
                <span className="cl-suffix">원</span>
              </div>
            </div>
          </div>

          <div className="cl-form-row">
            <label className="cl-label">수강권 타입</label>
            <Select value={ticketType} onChange={(e) => setTicketType(e.target.value as TicketType)} options={TICKET_TYPE_OPTIONS} />
          </div>

          <div className="cl-form-row">
            <label className="cl-label">이론 필수 Day</label>
            <input className="cl-input" type="number" min="1" placeholder="미설정" value={theoryRequiredBeforeDay} onChange={(e) => setTheoryRequiredBeforeDay(e.target.value)} />
          </div>

          <div className="cl-form-grid-3">
            <div className="cl-form-row">
              <label className="cl-label">판매 여부</label>
              <Select
                value={String(isActive)}
                onChange={(e) => setIsActive(e.target.value === "true")}
                options={[
                  { value: "true", label: "판매 중" },
                  { value: "false", label: "판매 중지" },
                ]}
              />
            </div>
            <div className="cl-form-row">
              <label className="cl-label">추천 상품</label>
              <Select
                value={String(isFeatured)}
                onChange={(e) => setIsFeatured(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오" },
                  { value: "true", label: "예" },
                ]}
              />
            </div>
            <div className="cl-form-row">
              <label className="cl-label">재수강 상품</label>
              <Select
                value={String(isRetakeProduct)}
                onChange={(e) => setIsRetakeProduct(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오" },
                  { value: "true", label: "예" },
                ]}
              />
            </div>
          </div>

          <div className="cl-modal-info__actions">
            <Button variant="outline" className="btn btn--lg " onClick={onClose}>
              취소
            </Button>
            <Button variant="primary" className="btn btn--lg " disabled={saving} onClick={handleSave}>
              {saving ? "저장 중..." : "저장"}
            </Button>
          </div>
        </div>
      )}

      {/* 강습 옵션 탭 */}
      {activeTab === "options" && <OptionsPanel productId={product.id} />}
    </>
  );
};

export default ClassEditModal;
